import { create } from 'zustand';
import {
  listMemos, createMemo, patchMemo, deleteMemo,
  MemoConflictError, type MemoDto,
} from '../api/memos';
import type { MemoContext } from '../lib/memo-context';

/**
 * memoStore (design-20260724-rightrail-memo) — 메모 **데이터**(목록 2그룹 · draft · 자동 저장).
 * 패널 표시 상태는 memoPanelStore 가 소유한다.
 *
 * 자동 저장(§4.7):
 *   - 타이핑 → setDraft 즉시 로컬 반영 → debounce 800ms 후 서버 저장
 *   - 닫기·목록 복귀·라우트 전환 시 flush() 로 debounce 무시 즉시 저장
 *   - 빈 메모는 서버에 만들지 않는다 — 첫 글자 입력 시 POST 로 승격
 *   - 실패 시 draft 를 localStorage 에 보관 → 다음 오픈 때 복구
 */

const SAVE_DEBOUNCE_MS = 800;
const LS_DRAFT = 'memo.draft.v1';

export type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

export interface MemoDraft {
  /** 서버 id. null = 아직 저장 전(빈 메모). */
  id: string | null;
  title: string;
  body: string;
  /** 마지막으로 받은 서버 updatedAt — 409 충돌 감지 기준(§6). */
  baseUpdatedAt: string | null;
  /** 새 메모일 때 함께 저장할 작성 화면 컨텍스트. */
  context: MemoContext | null;
}

function persistDraft(draft: MemoDraft | null): void {
  try {
    if (draft && (draft.title !== '' || draft.body !== '')) {
      localStorage.setItem(LS_DRAFT, JSON.stringify(draft));
    } else {
      localStorage.removeItem(LS_DRAFT);
    }
  } catch {
    /* localStorage 비활성 — 세션 내 동작은 유지 */
  }
}

function readDraft(): MemoDraft | null {
  try {
    const raw = localStorage.getItem(LS_DRAFT);
    if (!raw) return null;
    const d = JSON.parse(raw) as MemoDraft;
    return d && typeof d.body === 'string' ? d : null;
  } catch {
    return null;
  }
}

function clearDraft(): void {
  try { localStorage.removeItem(LS_DRAFT); } catch { /* ignore */ }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

interface MemoState {
  /** '이 화면' 그룹(§4.9). */
  contextMemos: MemoDto[];
  contextTotal: number;
  /** '전체' 그룹 — 고정 포함, 컨텍스트 그룹 제외. */
  memos: MemoDto[];
  nextCursor: string | null;
  loading: boolean;
  error: string | null;

  draft: MemoDraft | null;
  saveState: SaveState;
  savedAt: string | null;
  /** 409 충돌 시 서버 최신본 — 배너로 병합 선택지를 제시한다. */
  conflict: MemoDto | null;

  refresh: (opts: { query?: string; contextKey?: string | null }) => Promise<void>;
  loadMore: (opts: { query?: string; contextKey?: string | null }) => Promise<void>;
  /** 컨텍스트 그룹 상한(10) 초과분까지 펼치기 — '더 보기'. */
  expandContextGroup: (contextKey: string) => Promise<void>;

  startDraft: (context: MemoContext | null) => void;
  editExisting: (memo: MemoDto) => void;
  setDraft: (patch: { title?: string; body?: string }) => void;
  /** debounce 무시 즉시 저장 — 닫기/목록복귀/라우트전환 시. */
  flush: () => Promise<void>;
  discardDraft: () => void;

  togglePin: (memo: MemoDto) => Promise<void>;
  remove: (id: string) => Promise<void>;
  resolveConflict: (mode: 'overwrite' | 'takeServer') => Promise<void>;
}

export const useMemoStore = create<MemoState>((set, get) => ({
  contextMemos: [],
  contextTotal: 0,
  memos: [],
  nextCursor: null,
  loading: false,
  error: null,

  draft: readDraft(),
  saveState: 'idle',
  savedAt: null,
  conflict: null,

  refresh: async ({ query, contextKey }) => {
    set({ loading: true, error: null });
    try {
      const res = await listMemos({ query, contextKey });
      set({
        contextMemos: res.contextMemos,
        contextTotal: res.contextTotal,
        memos: res.memos,
        nextCursor: res.nextCursor,
        loading: false,
      });
    } catch (err) {
      set({ loading: false, error: (err as Error)?.message ?? '목록을 불러오지 못했습니다' });
    }
  },

  loadMore: async ({ query, contextKey }) => {
    const { nextCursor, memos, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const res = await listMemos({ query, contextKey, cursor: nextCursor });
      set({ memos: [...memos, ...res.memos], nextCursor: res.nextCursor, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error)?.message ?? '더 불러오지 못했습니다' });
    }
  },

  expandContextGroup: async (contextKey) => {
    try {
      // 컨텍스트 그룹 전체를 받아오는 별도 조회 — 상한 없이 그 화면 메모만.
      const res = await listMemos({ contextKey, limit: 100 });
      set({ contextMemos: res.contextMemos, contextTotal: res.contextTotal });
    } catch {
      /* 실패해도 기존 목록 유지 */
    }
  },

  startDraft: (context) => {
    const draft: MemoDraft = { id: null, title: '', body: '', baseUpdatedAt: null, context };
    set({ draft, saveState: 'idle', conflict: null });
    persistDraft(draft);
  },

  editExisting: (memo) => {
    const draft: MemoDraft = {
      id: memo.id,
      title: memo.title ?? '',
      body: memo.body,
      baseUpdatedAt: memo.updatedAt,
      context: null,
    };
    set({ draft, saveState: 'idle', conflict: null });
    persistDraft(draft);
  },

  setDraft: (patch) => {
    const cur = get().draft;
    if (!cur) return;
    const next: MemoDraft = { ...cur, ...patch };
    set({ draft: next });
    persistDraft(next);

    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => { void get().flush(); }, SAVE_DEBOUNCE_MS);
  },

  flush: async () => {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    const draft = get().draft;
    if (!draft) return;
    // 빈 메모는 서버에 만들지 않는다(빈 껍데기 축적 방지).
    if (draft.id === null && draft.title === '' && draft.body === '') return;

    set({ saveState: 'saving' });
    try {
      let saved: MemoDto;
      if (draft.id === null) {
        saved = await createMemo({
          title: draft.title || undefined,
          body: draft.body,
          contextKey: draft.context?.contextKey ?? undefined,
          contextPath: draft.context?.contextPath ?? undefined,
          contextLabel: draft.context?.contextLabel ?? undefined,
        });
      } else {
        saved = await patchMemo(draft.id, {
          title: draft.title === '' ? null : draft.title,
          body: draft.body,
          baseUpdatedAt: draft.baseUpdatedAt ?? undefined,
        });
      }
      set({
        draft: { ...get().draft!, id: saved.id, baseUpdatedAt: saved.updatedAt },
        saveState: 'saved',
        savedAt: saved.updatedAt,
        conflict: null,
      });
      clearDraft();
    } catch (err) {
      if (err instanceof MemoConflictError) {
        // 조용한 덮어쓰기 금지 — 사용자가 선택하도록 배너로 넘긴다.
        set({ saveState: 'failed', conflict: err.serverMemo });
        return;
      }
      // 실패해도 draft 는 localStorage 에 남아 다음 오픈 때 복구된다.
      set({ saveState: 'failed' });
      persistDraft(get().draft);
    }
  },

  discardDraft: () => {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    clearDraft();
    set({ draft: null, saveState: 'idle', conflict: null });
  },

  togglePin: async (memo) => {
    const next = !memo.pinned;
    // 낙관적 반영 — 실패 시 refresh 로 롤백은 호출부(목록 재조회)가 담당.
    set({
      memos: get().memos.map((m) => (m.id === memo.id ? { ...m, pinned: next } : m)),
      contextMemos: get().contextMemos.map((m) => (m.id === memo.id ? { ...m, pinned: next } : m)),
    });
    try {
      await patchMemo(memo.id, { pinned: next });
    } catch {
      set({
        memos: get().memos.map((m) => (m.id === memo.id ? { ...m, pinned: memo.pinned } : m)),
        contextMemos: get().contextMemos.map((m) => (m.id === memo.id ? { ...m, pinned: memo.pinned } : m)),
      });
    }
  },

  remove: async (id) => {
    set({
      memos: get().memos.filter((m) => m.id !== id),
      contextMemos: get().contextMemos.filter((m) => m.id !== id),
    });
    try {
      await deleteMemo(id);
    } catch {
      /* 실패 시 다음 refresh 에서 되살아난다(soft delete 라 데이터 유실 없음) */
    }
  },

  resolveConflict: async (mode) => {
    const { conflict, draft } = get();
    if (!conflict || !draft) return;
    if (mode === 'takeServer') {
      const next: MemoDraft = {
        ...draft,
        title: conflict.title ?? '',
        body: conflict.body,
        baseUpdatedAt: conflict.updatedAt,
      };
      set({ draft: next, conflict: null, saveState: 'saved', savedAt: conflict.updatedAt });
      persistDraft(next);
      return;
    }
    // overwrite — 서버 최신 시각을 기준으로 삼아 다시 저장(내 내용 유지).
    set({ draft: { ...draft, baseUpdatedAt: conflict.updatedAt }, conflict: null });
    await get().flush();
  },
}));
